import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetDatasetDataMutation,
  useMutationDatasetMetadata
} from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import AdminAreaSelection from "@/components/adminAreaSelection";
import NotificationContent from "@/components/notification/notificationContent";
import Divider from "@/components/ui/Divider";
import { MAX_POI } from "@/constants/dataset";
import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import {
  getAdminAreaRequired,
  getAdminAreaVisibility
} from "@/helpers/datasetAttributeHelper";
import { useAttributeFilter } from "@/hooks/attributeFilter/useAttributeFilter";
import { useH3Configuration } from "@/hooks/h3Configuration/useH3Configuration";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDatasetStream } from "@/hooks/useDatasetStream";
import { RootState } from "@/store";
import { addLinkedDatasetId } from "@/store/slice/globalFilterSlice";
import { setDatasetConfigTemp } from "@/store/slice/spatialSlice";
import {
  ESidebarMenuType,
  setSideBarMenuTypePopupDisplay
} from "@/store/slice/uiSlice";
import {
  DatasetData,
  DatasetDetailResponse,
  DatasetFilter,
  DatasetMetadata
} from "@/types/dataset";
import { generateDatasetId } from "@/utils/common";
import {
  convertDatasetResponseToFeatureCollection,
  createNewDataset
} from "@/utils/dataset";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import AggregationFilter from "./AggregationFilter";
import { AttributeFilter } from "./AttributeFilter";
import EmptyState from "./EmptyState";
import Header from "./Header";
import SavedFiltersList from "./SavedFiltersList";

const TEST_ID_PREFIX = "dataset-explorer-attribute-filter";

type DatasetRightAttrFilterProps = {
  disabled?: boolean;
  selectedDataset: DatasetDetailResponse | null;
  source?: string;
  isSubscribed?: boolean;
  isLoading?: boolean;
  onClose: () => void;
  totalRows?: number;
  datasetData?: DatasetData;
};

const CatchmentErrorMessage = ({ datasetLength = 0 }) => (
  <span className="text-cinnabar text-xs mb-2">
    {datasetLength === 0
      ? "No data selected. Please select at least one record to proceed."
      : `Selection exceeds the limit (${datasetLength}/${MAX_POI}). Please reduce the number of records to proceed.`}
  </span>
);

const DatasetRightAttrFilter = ({
  disabled = false,
  selectedDataset,
  isSubscribed = true,
  isLoading = false,
  totalRows = 0,
  datasetData: defaultDatasetData,
  onClose
}: DatasetRightAttrFilterProps) => {
  const [isLoadingDataset, setIsLoadingDataset] = useState(false);
  const [datasetData, setDatasetData] = useState<DatasetData | undefined>();
  const [datasetStreamId, setDatasetStreamId] = useState<string | undefined>();
  const [isOverrideAdminArea, setIsOverrideAdminArea] = useState(false);

  const { isUseNewDatasetExplorerModal } = useAppFeatures();
  const dispatch = useDispatch();
  const { sideBarMenuTypePopupDisplay, showCatchmentPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const { adminAreaSettings: globalAdminArea } = useSelector(
    (state: RootState) => state.globalFilter
  );
  const { handleAddDataset } = useDataset();
  const { handleMovePositionToLayer } = useMap();
  const { showLoading, showError, showSuccess } = useLoadingAndToast();
  const { data: userProfileData } = useGetUserProfileAPI();
  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata();

  const handleErrorGetDataset = () => {
    setIsLoadingDataset(false);
    if (showCatchmentPopup) {
      showError(
        <NotificationContent
          title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
          description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
        />
      );
    } else {
      showError(
        <NotificationContent title={MESSAGE.DATASET_ADD_TITLE_FAILED}>
          <p className="mt-1">
            {MESSAGE.DATASET_ADD_DESCRIPTION_PREFIX}
            <span className="font-semibold mx-1">{selectedDataset?.name}</span>
            {MESSAGE.DATASET_ADD_DESCRIPTION_FAILED}
          </p>
        </NotificationContent>
      );
    }
  };

  const { mutateAsync: getDatasetDataPost } = useGetDatasetDataMutation({
    timezone: userProfileData?.timezone,
    options: {
      onError: () => {
        handleErrorGetDataset();
      },
      onSuccess: (data) => {
        setDatasetData(data);
      }
    }
  });
  const { h3Configuration, setH3Configuration } = useH3Configuration({
    selectedDataset
  });
  const {
    isGettingAttributeValues,
    attributeValues,
    defaultSelectedValue,
    filterOptions,
    selectedOptions,
    savedFilters,
    showFilters,
    handleAddAttributeFilter,
    getAvailableFilterOptions,
    initialAdminArea,
    isAttributeFilterValid,
    handleSaveFilter,
    handleEditFilter,
    handleDeleteFilter,
    selectAttributeItem,
    handleSearchChange,
    handleSelectFilter,
    handleAdminAreaChange,
    handleBack,
    searchTerm,
    selectedFilter,
    onFilterChange,
    selectedAdminArea,
    setSelectedAdminArea,
    adminAreaValid,
    isAdminAreaSelection,
    isAdminAreaSelectionValid
  } = useAttributeFilter({
    selectedDataset,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timezone: userProfileData?.timezone as any
  });

  useDatasetStream({
    datasetId: datasetStreamId,
    enabled: !!datasetStreamId,
    timezone: userProfileData?.timezone,
    filters: savedFilters,
    h3Configuration,
    onOpen: () => {
      setIsLoadingDataset(true);
      showLoading(
        <NotificationContent description={MESSAGE.DATASET_ADD_LOADING} />
      );
    },
    onStreamComplete: async (data) => {
      if (!selectedDataset) return;
      setIsLoadingDataset(true);
      try {
        const currentDatasetData = data.rawDataset;
        const attributesSchema = data.datasetMetadata;

        if (!currentDatasetData || !attributesSchema) return;

        if (
          showCatchmentPopup &&
          currentDatasetData.rows.length <= MAX_POI &&
          catchmentSettingsTemp?.datasetConfig
        ) {
          handleAddDatasetShowCatchment(currentDatasetData, attributesSchema);
        } else {
          const newDataset = await handleAddDataset({
            datasetMetadata: selectedDataset, // full dataset schema from metadata query
            datasetData: currentDatasetData,
            savedFilters,
            attributesSchema: attributesSchema.schema, // filtered schema from dataset_detail when grid_config is present
            h3Configuration
          });
          if (isAdminAreaSelection && !isOverrideAdminArea) {
            dispatch(
              addLinkedDatasetId({
                datasetId: newDataset.data.id
              })
            );
          }
        }
        if (showCatchmentPopup && currentDatasetData.rows.length > MAX_POI) {
          setIsLoadingDataset(false);
          return;
        }
        onClose();
        if (sideBarMenuTypePopupDisplay !== ESidebarMenuType.SPATIAL) {
          dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));
        }
      } catch {
        return; // catch block only returns here because the error will be handled by the onError above
      }
      // Wait for dataset to render before showing success notification
      showSuccess(
        <NotificationContent title={MESSAGE.DATASET_ADD_TITLE_SUCCESS}>
          <p className="mt-1">
            {MESSAGE.DATASET_ADD_DESCRIPTION_PREFIX}
            <span className="font-semibold mx-1">{selectedDataset?.name}</span>
            {MESSAGE.DATASET_ADD_DESCRIPTION_SUCCESS}
          </p>
        </NotificationContent>
      );

      setIsLoadingDataset(false);
      setDatasetStreamId(undefined);
    },
    onError: () => {
      setIsLoadingDataset(false);
      setDatasetStreamId(undefined);
      handleErrorGetDataset();
    }
  });

  useEffect(() => {
    setSelectedAdminArea(globalAdminArea);
    if (filterOptions) {
      handleAdminAreaChange(globalAdminArea);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalAdminArea, filterOptions]);

  const handleAddDatasetShowCatchment = (
    currentDatasetData: DatasetData,
    attributesSchema: DatasetMetadata
  ) => {
    if (!selectedDataset || !catchmentSettingsTemp?.datasetConfig) return;
    const { id, nextIndex } = generateDatasetId(
      selectedDataset.id,
      catchmentSettingsTemp?.datasetConfig?.datasetIndices
    );
    const datasetName =
      nextIndex > 1
        ? `${selectedDataset.name} (${nextIndex - 1})`
        : selectedDataset.name;
    const newDataset = createNewDataset({
      dataset: selectedDataset,
      datasetId: id,
      datasetName: datasetName,
      attributesSchema: attributesSchema.schema
    });
    const featureCollection =
      convertDatasetResponseToFeatureCollection(currentDatasetData);
    handleMovePositionToLayer(featureCollection);
    dispatch(
      setDatasetConfigTemp({
        uniqueId: id,
        originalId: selectedDataset.id,
        dataset: newDataset,
        rawDataset: currentDatasetData,
        filters: savedFilters,
        nextIndex
      })
    );
  };

  const onAddDataset = async () => {
    if (!selectedDataset?.id) return;

    if (datasetData || defaultDatasetData) {
      const attributesSchema = await getDatasetMetadata({
        id: selectedDataset?.id,
        h3Configuration
      });
      const currentDatasetData = datasetData ?? defaultDatasetData;

      if (
        showCatchmentPopup &&
        currentDatasetData &&
        currentDatasetData.rows.length <= MAX_POI &&
        catchmentSettingsTemp?.datasetConfig
      ) {
        handleAddDatasetShowCatchment(currentDatasetData, attributesSchema);
        onClose();
      }
    } else {
      setDatasetStreamId(selectedDataset?.id);
    }
  };

  const onSaveFilter = async () => {
    const newFilter = await handleSaveFilter();
    if (showCatchmentPopup && selectedDataset) {
      await getDatasetDataPost({
        id: selectedDataset.id,
        filter: newFilter,
        h3Configuration
      });
    }
  };

  const onDeleteFilter = async (filter: DatasetFilter) => {
    const filteredSavedFilter = handleDeleteFilter(filter);
    if (selectedDataset && showCatchmentPopup) {
      await getDatasetDataPost({
        id: selectedDataset.id,
        filter: filteredSavedFilter,
        h3Configuration
      });
    }
  };

  const disabledFilter = disabled || !isSubscribed;
  const showAdminAreaSelection = isAdminAreaSelection && isSubscribed;
  const showAggregationSelection = selectedDataset && isSubscribed;

  const totalRowsDataset = useMemo(
    () => datasetData?.rows?.length ?? totalRows,
    [datasetData?.rows?.length, totalRows]
  );

  const isInvalidPOILength = useMemo(
    () =>
      !!showCatchmentPopup &&
      (totalRowsDataset === 0 || totalRowsDataset > MAX_POI),
    [showCatchmentPopup, totalRowsDataset]
  );

  const showErrorPOICondition = useMemo(
    () => showCatchmentPopup && selectedDataset && isInvalidPOILength,
    [showCatchmentPopup, selectedDataset, isInvalidPOILength]
  );

  return (
    <div className="flex flex-col w-[20%]">
      <div className="flex flex-col h-[calc(100%-40px)] bg-white-smoke rounded-[12px]">
        <Header
          showBackButton={showFilters}
          disabled={disabledFilter}
          onBack={handleBack}
        />

        <div className="flex-1 flex flex-col min-h-0 mt-1 pt-3 px-3">
          {showFilters ? (
            <AttributeFilter
              savedFilters={savedFilters}
              getAvailableFilterOptions={getAvailableFilterOptions}
              handleSelectFilter={handleSelectFilter}
              selectAttributeItem={selectAttributeItem}
              defaultSelectedValue={defaultSelectedValue}
              attributeValues={attributeValues}
              selectedOptions={selectedOptions}
              searchTerm={searchTerm}
              isGettingAttributeValues={isGettingAttributeValues}
              onSearchChange={handleSearchChange}
              onChange={onFilterChange}
            />
          ) : (
            <>
              <p
                className={twMerge(
                  "text-[10px] text-txtBody mb-3",
                  disabledFilter ? "text-light-gray" : ""
                )}
              >
                You may add filters to the attributes based on the dataset you
                select.
              </p>
              {showAdminAreaSelection && (
                <AdminAreaSelection
                  selectedData={selectedAdminArea || initialAdminArea}
                  onSelectData={(newAdminArea) => {
                    setSelectedAdminArea(newAdminArea);
                    handleAdminAreaChange(newAdminArea);
                    setIsOverrideAdminArea(true);
                  }}
                  options={{
                    showCountryLevel: false,
                    showLabelAboveInput: false,
                    showLabel: true,
                    prefixLabel: "Select",
                    required: getAdminAreaRequired(selectedDataset),
                    visible: getAdminAreaVisibility(selectedDataset)
                  }}
                />
              )}
              <div className="flex-1 flex flex-col overflow-auto">
                {showAggregationSelection && (
                  <AggregationFilter
                    onChangeAggregationType={(value) => {
                      setH3Configuration((prev) => ({
                        ...prev,
                        aggregationType: value
                      }));
                    }}
                    value={h3Configuration}
                    spatialType={selectedDataset?.spatial_aggregation_type}
                    dataSource={selectedDataset?.source}
                  />
                )}
                {!isUseNewDatasetExplorerModal && !showAggregationSelection && (
                  <EmptyState />
                )}
                {!isUseNewDatasetExplorerModal && <Divider className="my-3" />}
                {showErrorPOICondition && !isLoading && (
                  <CatchmentErrorMessage datasetLength={totalRowsDataset} />
                )}
                <Button
                  disabled={
                    disabledFilter ||
                    savedFilters.length === filterOptions.length ||
                    !adminAreaValid
                  }
                  className={twMerge(
                    "h-[40px] w-full border-teal-500 bg-white text-teal-500  border text-sm font-bold disabled:border-gainsboro disabled:border disabled:text-gainsboro mb-3",
                    isUseNewDatasetExplorerModal && showAdminAreaSelection
                      ? "mt-3"
                      : ""
                  )}
                  onClick={handleAddAttributeFilter}
                >
                  Add Attribute Filter
                </Button>
                {isUseNewDatasetExplorerModal && !showAggregationSelection && (
                  <EmptyState />
                )}
                {savedFilters.length > 0 && (
                  <SavedFiltersList
                    filterOptions={filterOptions}
                    filters={savedFilters}
                    onEdit={handleEditFilter}
                    onDelete={onDeleteFilter}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="mt-4">
        {showFilters && selectedFilter ? (
          <Button
            variant="secondary"
            disabled={
              showFilters ? !isAttributeFilterValid : !isAdminAreaSelectionValid
            }
            className="w-full py-2 border-teal-500 bg-white text-teal-500  text-sm h-auto font-bold disabled:bg-white disabled:text-gainsboro disabled:border disabled:border-gainsboro "
            onClick={onSaveFilter}
            data-testid={`${TEST_ID_PREFIX}-save-button`}
          >
            Save
          </Button>
        ) : (
          !showFilters && (
            <Button
              disabled={
                disabledFilter ||
                isLoadingDataset ||
                !adminAreaValid ||
                isInvalidPOILength
              }
              className="w-full h-[40px] py-2 border-teal-500 bg-teal-500 text-white text-sm font-bold disabled:bg-gainsboro disabled:border-none flex-none"
              onClick={onAddDataset}
              data-testid={`${TEST_ID_PREFIX}-add-dataset-button`}
            >
              Add Dataset
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default DatasetRightAttrFilter;
